import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Card } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "react-calendar/dist/Calendar.css";
import Calendar from "react-calendar";
import dayjs from "dayjs";

const ClickStatsChart = ({ data, params, setParams }) => {
  const [timeFrame, setTimeFrame] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState([null, null]);

  const isValidData = (data) => {
    return (
      Array.isArray(data) &&
      data.every(
        (item) =>
          item &&
          typeof item.created_day === "string" &&
          !isNaN(Date.parse(item.created_day)) &&
          typeof item.clicks === "number" &&
          item.clicks >= 0
      )
    );
  };

  const groupDataByTimeFrame = (data, timeFrame) => {
    const groupedData = [];

    const groupByMonth = (data) => {
      const grouped = {};
      data.forEach((item) => {
        const date = new Date(item.created_day);
        const monthYear = `${date.getMonth() + 1}-${date.getFullYear()}`;
        if (!grouped[monthYear]) {
          grouped[monthYear] = 0;
        }
        grouped[monthYear] += item.clicks;
      });

      for (const monthYear in grouped) {
        const [month, year] = monthYear.split("-");
        groupedData.push({
          month: parseInt(month),
          year: parseInt(year),
          clicks: grouped[monthYear],
        });
      }
    };

    const groupByWeek = (data) => {
      const grouped = {};
      data?.forEach((item) => {
        const date = new Date(item.created_day);
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay());
        const weekStartDate = startOfWeek.toISOString().split("T")[0];

        if (!grouped[weekStartDate]) {
          grouped[weekStartDate] = 0;
        }
        grouped[weekStartDate] += item.clicks;
      });

      for (const weekStartDate in grouped) {
        groupedData.push({
          date: weekStartDate,
          clicks: grouped[weekStartDate],
        });
      }
    };

    const groupByYear = (data) => {
      const grouped = {};
      const availableYears = [];

      data?.forEach((item) => {
        const date = new Date(item.created_day);
        const year = date.getFullYear();
        availableYears.push(year);
        if (!grouped[year]) {
          grouped[year] = 0;
        }
        grouped[year] += item.clicks;
      });

      const minYear = Math.min(...availableYears);
      const maxYear = Math.max(...availableYears);
      for (let year = minYear; year <= maxYear; year++) {
        if (grouped[year]) {
          groupedData.push({ year: year, clicks: grouped[year] });
        } else {
          groupedData.push({ year: year, clicks: 0 });
        }
      }
    };

    switch (timeFrame) {
      case "Monthly":
        groupByMonth(data);
        break;
      case "Weekly":
        groupByWeek(data);
        break;
      case "Yearly":
        groupByYear(data);
        break;
      case "All":
        return data;
      default:
        return data;
    }

    return groupedData;
  };

  const handleTimeFrameChange = (newTimeFrame) => {
    setTimeFrame(newTimeFrame);
    setSelectedRange([null, null]);

    setIsDropdownOpen(false);
    if (newTimeFrame === "All") {
      setParams({
        start_date: null,
        end_date: null,
      });
    }
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedMonth(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedMonth(newDate);
  };

  const dropdownOptions = ["All", "Monthly", "Weekly", "Yearly", "Custom"];

  const filteredData = useMemo(() => {
    if (isValidData(data)) {
      if (timeFrame === "Custom") {
        if (selectedRange[0] && selectedRange[1]) {
          const startDate = new Date(selectedRange[0]);
          const endDate = new Date(selectedRange[1]);

          // Filter the data to include only the items within the selected range
          return data.filter((item) => {
            const itemDate = new Date(item.created_day);
            return itemDate >= startDate && itemDate <= endDate;
          });
        } else {
          // If no date range is selected, show data for the selected month
          return data.filter((item) => {
            const itemDate = new Date(item.created_day);
            return (
              itemDate.getFullYear() === selectedMonth.getFullYear() &&
              itemDate.getMonth() === selectedMonth.getMonth()
            );
          });
        }
      }

      // If no date range is selected, group by the chosen time frame
      return groupDataByTimeFrame(data, timeFrame);
    }
    return [];
  }, [data, timeFrame, selectedRange, selectedMonth]);

  const options = {
    colors: ["#0D56EB"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.2,
        gradientToColors: ["#0D56EB"],
        inverseColors: false,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    grid: {
      borderColor: "#393939",
      strokeDashArray: 0,
    },
    chart: {
      height: 350,
      type: "area",
      zoom: { enabled: true },
      toolbar: { show: false }, // Removes the toolbar
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: filteredData.map((item) => {
        if (timeFrame === "Custom") {
          // For Custom, generate the categories from the selected range
          const itemDate = new Date(item.created_day);
          return itemDate.toLocaleDateString(); // or format as per your requirement
        } else if (timeFrame === "Monthly") {
          const date = new Date(item.year, item.month - 1);
          return date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
        } else if (timeFrame === "Weekly") {
          return item.date;
        } else if (timeFrame === "Yearly") {
          return item.year;
        } else {
          return item.created_day;
        }
      }),
    },
    tooltip: {
      x: { formatter: (val) => `` },
      y: {
        formatter: (val) => parseInt(val),
      },
      enabled: true,
      shared: true,
      followCursor: false,
      intersect: false,
    },
  };

  const series = [
    {
      name: "Clicks",
      type: "area",
      data: filteredData.map((item) => Math.round(item.clicks)),
    },
  ];

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    setIsCalendarOpen(false);
    setParams({
      start_date: dayjs(range[0]).format("DD/MMM/YYYY"),
      end_date: dayjs(range[1]).format("DD/MMM/YYYY"),
    });
  };

  const formatSelectedRange = (range) => {
    if (!range[0] || !range[1]) return "";
    const startDate = dayjs(range[0]).format("DD-MM-YYYY");
    const endDate = dayjs(range[1]).format("DD-MM-YYYY");
    return `${startDate} - ${endDate}`;
  };

  // Logic for displaying the dropdown label
  const dropdownLabel =
    selectedRange[0] && selectedRange[1] ? "Custom" : timeFrame; // Always display "Custom" in the dropdown when no date range is selected

  return (
    <Card className="pushleads-chart">
      <div className="filter_wrapper">
        <div className="date_filter">
          <div onClick={handlePreviousMonth} style={{ cursor: "pointer" }}>
            <ArrowBackIosIcon color="white" fontSize="small" />
          </div>

          {/* Display Current Month or Selected Date Range */}
          <span onClick={() => setIsCalendarOpen(true)}>
            {selectedRange[0] && selectedRange[1]
              ? formatSelectedRange(selectedRange)
              : selectedMonth.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
          </span>

          <div onClick={handleNextMonth} style={{ cursor: "pointer" }}>
            <ArrowForwardIosIcon color="white" fontSize="small" />
          </div>
        </div>

        <div className="dropdown_wrapper">
          <div
            className="dropdown"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span>{dropdownLabel}</span>
            {dropdownIcon}
          </div>
          {isDropdownOpen && (
            <div className="dropdown_options">
              {dropdownOptions.map((option) => (
                <div
                  key={option}
                  className="dropdown_option"
                  onClick={() => handleTimeFrameChange(option)}
                >
                  <span>{option}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isCalendarOpen && (
        <div className="aff_calendar_wrapper dateCalendarContainer">
          <Calendar
            selectRange
            onChange={handleRangeSelect}
            value={selectedRange}
            calendarType="US"
          />
        </div>
      )}

      <ReactApexChart
        className="reactChart_div"
        options={options}
        series={series}
        type="area"
        height={220}
      />
    </Card>
  );
};

ClickStatsChart.propTypes = {
  data: PropTypes.array.isRequired,
};

export default ClickStatsChart;

const dropdownIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="5"
    viewBox="0 0 8 5"
    fill="none"
  >
    <path
      d="M3.99891 5.00028C3.82129 5.00028 3.64366 4.94277 3.50343 4.81955L0.203334 1.9196C-0.0677781 1.68136 -0.0677781 1.28704 0.203334 1.0488C0.474447 0.810557 0.923185 0.810557 1.1943 1.0488L3.99891 3.51334L6.80352 1.0488C7.07464 0.810557 7.52338 0.810557 7.79449 1.0488C8.0656 1.28704 8.0656 1.68136 7.79449 1.9196L4.49439 4.81955C4.35416 4.94277 4.17654 5.00028 3.99891 5.00028Z"
      fill="url(#paint0_radial_3910_94474)"
    />
    <defs>
      <radialGradient
        id="paint0_radial_3910_94474"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(0.0975342 4.68257) rotate(-24.5812) scale(8.68762 17.8457)"
      >
        <stop stop-color="#0D56EB" />
        <stop offset="1" stop-color="#0640B9" />
      </radialGradient>
    </defs>
  </svg>
);
